/**
 * `/finance/payouts` — cash payout maker/checker (D04, §10.5): request, approve (maker ≠ checker, dual approval
 * above the tenant threshold, step-up, evidence attached), reject, record a manual transfer, retry a rejected
 * payout as a new attempt. Destinations stay masked; unmasking goes through `PAYOUT_DESTINATION` with a purpose.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PayoutDetailDto } from '@windrop/contracts';
import { Button, Field, Input, Modal, useIntl, useToast } from '@windrop/ui';
import { useState } from 'react';
import { Can } from '../../auth/guards';
import { useStepUp } from '../../auth/step-up';
import { MaskedCell } from '../../components/MaskedCell';
import { PageHeader } from '../../components/PageHeader';
import { QueueList } from '../../components/QueueList';
import { ToneBadge } from '../../components/StatusBadge';
import { financeApi } from '../../lib/api/finance';
import { isApiError } from '../../lib/api/client';
import { FinanceNav } from './FinanceNav';

type Action = { kind: 'approve' | 'reject' | 'complete' | 'retry'; payout: PayoutDetailDto };

export function PayoutsPage() {
  const intl = useIntl();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { withStepUp } = useStepUp();
  const [action, setAction] = useState<Action | null>(null);
  const [evidenceRef, setEvidenceRef] = useState('');
  const [note, setNote] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [destinationId, setDestinationId] = useState('');
  const [claimId, setClaimId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({ queryKey: ['finance', 'payouts'], queryFn: () => financeApi.payouts() });
  const done = (title: string) => {
    setAction(null);
    setEvidenceRef('');
    setNote('');
    setBankReference('');
    setDestinationId('');
    void qc.invalidateQueries({ queryKey: ['finance'] });
    toast({ title, tone: 'success' });
  };
  const fail = (e: unknown) => {
    if (e instanceof Error && e.name === 'StepUpCancelled') return;
    setError(isApiError(e) ? e.message : 'Action failed.');
  };
  const act = useMutation({
    mutationFn: async () => {
      const a = action!;
      const id = a.payout.payoutId;
      const opts = {
        permission: 'finance.payout.approve' as const,
        reason: `Payout ${id.slice(-8)}: ${a.kind}.`,
      };
      if (a.kind === 'approve')
        return withStepUp(
          () =>
            financeApi.approvePayout(id, {
              ...(evidenceRef ? { evidenceRef } : {}),
              ...(note ? { note } : {}),
            }),
          opts,
        );
      if (a.kind === 'reject') return withStepUp(() => financeApi.rejectPayout(id, note), opts);
      if (a.kind === 'complete')
        return withStepUp(
          () =>
            financeApi.completePayout(id, {
              bankReference,
              ...(evidenceRef ? { evidenceRef } : {}),
              ...(note ? { note } : {}),
            }),
          opts,
        );
      return withStepUp(
        () =>
          financeApi.retryPayout(id, {
            ...(destinationId ? { destinationId } : {}),
            ...(note ? { note } : {}),
          }),
        opts,
      );
    },
    onSuccess: (p) => done(`Payout ${p.payoutId.slice(-8)}: ${p.status.toLowerCase()} — ${p.nextAction}`),
    onError: fail,
  });
  const create = useMutation({
    mutationFn: () =>
      withStepUp(
        () =>
          financeApi.createPayout({
            claimId,
            ...(destinationId ? { destinationId } : {}),
            ...(evidenceRef ? { evidenceRef } : {}),
            ...(note ? { note } : {}),
          }),
        { permission: 'finance.payout.approve', reason: 'Request a cash payout.' },
      ),
    onSuccess: (p) => {
      setClaimId('');
      done(`Payout requested (${p.requiresDualApproval ? 'dual approval' : 'single approval'} required)`);
    },
    onError: fail,
  });
  const items = (query.data?.items ?? []).map((p) => ({
    id: p.payoutId,
    title: `${intl.formatMoney(p.amount.amountMinor, p.amount.currency)} · win ${p.winId} · attempt ${p.attempts}${p.requiresDualApproval ? ' · dual approval' : ''}`,
    subtitle: (
      <>
        Destination{' '}
        <MaskedCell
          masked={p.destinationMasked}
          subject={{ kind: 'PAYOUT_DESTINATION', payoutId: p.payoutId }}
          what="payout destination"
        />{' '}
        · approvals {p.approvals.length}/{p.requiresDualApproval ? 2 : 1} · evidence{' '}
        {p.evidenceRefs.length ? 'attached' : 'missing'} · {p.participantSafeExplanation}
      </>
    ),
    since: p.createdAt,
    owner: p.owner,
    status: {
      tone:
        p.status === 'COMPLETED'
          ? ('success' as const)
          : p.status === 'REJECTED' || p.status === 'FAILED'
            ? ('error' as const)
            : ('warning' as const),
      text: p.status,
    },
    nextAction: {
      label:
        p.status === 'PENDING_APPROVAL'
          ? 'Review and approve'
          : p.status === 'REJECTED' || p.status === 'FAILED'
            ? 'Retry as new attempt'
            : p.status === 'APPROVED' && !p.providerPayouts
              ? 'Record transfer'
              : p.nextAction,
      onClick: () => {
        setError(null);
        setAction({
          kind:
            p.status === 'PENDING_APPROVAL'
              ? 'approve'
              : p.status === 'REJECTED' || p.status === 'FAILED'
                ? 'retry'
                : 'complete',
          payout: p,
        });
      },
    },
  }));
  const a = action;
  return (
    <>
      <PageHeader
        title="Finance — payouts"
        description="Cash prizes are paid manually with maker/checker approval; dual approval above the configured threshold; evidence attached before completion. Destinations are masked (unmask with a purpose)."
        breadcrumb={[{ label: 'Finance', to: '/finance' }, { label: 'Payouts' }]}
      />
      <FinanceNav />
      <section aria-labelledby="payout-queue">
        <h2 id="payout-queue">Payout queue</h2>
        <QueueList items={items} emptyText="No payouts requested." />
      </section>
      <Can permission="finance.payout.approve">
        <section
          aria-labelledby="payout-new"
          className="wd-surface"
          style={{ marginTop: 'var(--wd-space-5)', padding: 'var(--wd-space-4)' }}
        >
          <h2 id="payout-new">Request a payout (maker)</h2>
          <div className="wd-stack">
            <Field label="Claim id" required>
              <Input
                value={claimId}
                onChange={(e) => setClaimId(e.target.value.trim())}
                data-testid="payout-claim"
              />
            </Field>
            <Field label="Destination id" hint="Defaults to the claim's current destination.">
              <Input value={destinationId} onChange={(e) => setDestinationId(e.target.value.trim())} />
            </Field>
            <Field label="Evidence reference">
              <Input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} />
            </Field>
            <Button
              disabled={!claimId}
              onClick={() => create.mutate()}
              loading={create.isPending}
              loadingLabel="Requesting"
            >
              Request payout
            </Button>
          </div>
        </section>
      </Can>
      <Modal
        open={a !== null}
        onOpenChange={(o) => {
          if (!o) setAction(null);
        }}
        title={
          a ? `${a.kind[0]!.toUpperCase()}${a.kind.slice(1)} payout ${a.payout.payoutId.slice(-8)}` : 'Payout'
        }
        description={
          a
            ? `${intl.formatMoney(a.payout.amount.amountMinor, a.payout.amount.currency)} to ${a.payout.destinationMasked} · ${a.payout.nextAction}`
            : undefined
        }
      >
        {a ? (
          <div className="wd-stack">
            {a.kind === 'approve' ? (
              <>
                <p className="wd-muted">
                  Approving requires step-up. You cannot approve a payout you requested, and a second approver
                  is needed above the threshold.{' '}
                  <ToneBadge tone={a.payout.evidenceRefs.length ? 'success' : 'warning'}>
                    {a.payout.evidenceRefs.length ? 'Evidence attached' : 'Evidence required to complete'}
                  </ToneBadge>
                </p>
                <Field label="Evidence reference" hint="Object key of the transfer / claim evidence.">
                  <Input
                    value={evidenceRef}
                    onChange={(e) => setEvidenceRef(e.target.value)}
                    data-testid="payout-evidence"
                  />
                </Field>
              </>
            ) : null}
            {a.kind === 'complete' ? (
              <Field label="Bank reference" required>
                <Input value={bankReference} onChange={(e) => setBankReference(e.target.value)} />
              </Field>
            ) : null}
            {a.kind === 'retry' ? (
              <Field label="Corrected destination id" hint="Collected through the secure claim flow.">
                <Input value={destinationId} onChange={(e) => setDestinationId(e.target.value.trim())} />
              </Field>
            ) : null}
            {a.kind === 'reject' ? (
              <Field label="Reason" required>
                <Input value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            ) : (
              <Field label="Note">
                <Input value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            )}
            {error ? (
              <p role="alert" className="text-error">
                {error}
              </p>
            ) : null}
            <div className="wd-btn-group">
              <Button
                onClick={() => act.mutate()}
                loading={act.isPending}
                loadingLabel="Working"
                data-testid="payout-submit"
              >
                {a.kind === 'approve'
                  ? 'Approve with step-up'
                  : a.kind === 'reject'
                    ? 'Reject payout'
                    : a.kind === 'complete'
                      ? 'Record transfer'
                      : 'Create new attempt'}
              </Button>
              {a.kind === 'approve' ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setError(null);
                    setAction({ kind: 'reject', payout: a.payout });
                  }}
                >
                  Reject instead
                </Button>
              ) : null}
              <Button variant="quiet" onClick={() => setAction(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
